/**
 * Pure TEA update for the entity relationship create/edit dialog.
 *
 * Shell: `EntityLinksPanel.vue` (`useTea` + persist/remove Cmds).
 */
import { assertNever } from "../lib/assertNever";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type EntityLinkDialogModel = {
  readonly open: boolean;
  readonly sourceFile: string;
  readonly editingLinkId: Maybe<string>;
  readonly target: string;
  readonly relation: string;
  readonly note: string;
  readonly busy: boolean;
};

export const initialEntityLinkDialogModel: EntityLinkDialogModel = {
  open: false,
  sourceFile: "",
  editingLinkId: nothing,
  target: "",
  relation: "related_to",
  note: "",
  busy: false,
};

export type EntityLinkDialogMsg =
  | {
      readonly type: "OpenCreate";
      readonly sourceFile: string;
      readonly defaultTarget: string;
    }
  | {
      readonly type: "OpenEdit";
      readonly sourceFile: string;
      readonly linkId: string;
      readonly target: string;
      readonly relation: string;
      readonly note: string;
    }
  | { readonly type: "Close" }
  | { readonly type: "TargetChanged"; readonly target: string }
  | { readonly type: "RelationChanged"; readonly relation: string }
  | { readonly type: "NoteChanged"; readonly note: string }
  | { readonly type: "Commit" }
  | { readonly type: "RemoveEditing" }
  | {
      readonly type: "Remove";
      readonly sourceFile: string;
      readonly linkId: string;
    }
  | { readonly type: "SaveSucceeded" }
  | { readonly type: "SaveFailed" }
  | {
      readonly type: "RemoveSucceeded";
      readonly removedLinkId: string;
    }
  | { readonly type: "RemoveFailed" };

export type EntityLinkDialogCmd =
  | {
      readonly kind: "save";
      readonly sourceFile: string;
      readonly editingLinkId: Maybe<string>;
      readonly target: string;
      readonly relation: string;
      readonly note: string;
    }
  | {
      readonly kind: "remove";
      readonly sourceFile: string;
      readonly linkId: string;
    };

const closed = initialEntityLinkDialogModel;

export function updateEntityLinkDialog(
  model: EntityLinkDialogModel,
  msg: EntityLinkDialogMsg,
): readonly [EntityLinkDialogModel, readonly EntityLinkDialogCmd[]] {
  switch (msg.type) {
    case "OpenCreate":
      return [
        {
          open: true,
          sourceFile: msg.sourceFile,
          editingLinkId: nothing,
          target: msg.defaultTarget,
          relation: "related_to",
          note: "",
          busy: false,
        },
        [],
      ];
    case "OpenEdit":
      return [
        {
          open: true,
          sourceFile: msg.sourceFile,
          editingLinkId: just(msg.linkId),
          target: msg.target,
          relation: msg.relation,
          note: msg.note,
          busy: false,
        },
        [],
      ];
    case "Close":
      return [closed, []];
    case "TargetChanged":
      return [{ ...model, target: msg.target }, []];
    case "RelationChanged":
      return [{ ...model, relation: msg.relation }, []];
    case "NoteChanged":
      return [{ ...model, note: msg.note }, []];
    case "Commit": {
      const source = model.sourceFile.trim();
      const target = model.target.trim();
      const relation = model.relation.trim();
      if (
        source.length === 0 ||
        target.length === 0 ||
        relation.length === 0 ||
        model.busy
      ) {
        return [model, []];
      }
      return [
        { ...model, busy: true },
        [
          {
            kind: "save",
            sourceFile: source,
            editingLinkId: model.editingLinkId,
            target,
            relation,
            note: model.note.trim(),
          },
        ],
      ];
    }
    case "RemoveEditing": {
      if (model.busy || isNothing(model.editingLinkId)) {
        return [model, []];
      }
      return [
        { ...model, busy: true },
        [
          {
            kind: "remove",
            sourceFile: model.sourceFile,
            linkId: model.editingLinkId.value,
          },
        ],
      ];
    }
    case "Remove": {
      if (model.busy) return [model, []];
      return [
        { ...model, busy: true },
        [
          {
            kind: "remove",
            sourceFile: msg.sourceFile,
            linkId: msg.linkId,
          },
        ],
      ];
    }
    case "SaveSucceeded":
      return [closed, []];
    case "SaveFailed":
      return [{ ...model, busy: false }, []];
    case "RemoveSucceeded":
      if (
        isJust(model.editingLinkId) &&
        model.editingLinkId.value === msg.removedLinkId
      ) {
        return [closed, []];
      }
      return [{ ...model, busy: false }, []];
    case "RemoveFailed":
      return [{ ...model, busy: false }, []];
    default:
      return assertNever(msg);
  }
}

export const updateEntityLinkDialogFn: UpdateFn<
  EntityLinkDialogModel,
  EntityLinkDialogMsg,
  EntityLinkDialogCmd
> = updateEntityLinkDialog;
